import { Tweet } from "react-tweet";
import { ExternalLink } from "lucide-react";
import LinkPreviewCard from "./LinkPreviewCard";

// Detects a platform from a URL and returns a native embed config (or null if no embed available).
// Supported: X/Twitter (via react-tweet), YouTube, Spotify, SoundCloud, TikTok (static card),
// Instagram (static card), Apple Music, Bandcamp.
//
// PRIVACY: This component NEVER loads 3rd-party tracker scripts (widgets.js,
// embed.js, platform.twitter.com, etc.). Twitter renders via react-tweet's
// syndication API. TikTok & Instagram render as static "click-to-open" preview
// cards to protect user privacy.
const detectEmbed = (url) => {
  if (!url) return null;
  let u;
  try { u = new URL(url); } catch { return null; }
  const host = u.hostname.replace(/^www\./, "").toLowerCase();
  const path = u.pathname;

  // YouTube — first-party privacy-enhanced domain
  if (host === "youtube.com" || host === "m.youtube.com" || host === "youtu.be") {
    let videoId = null;
    if (host === "youtu.be") videoId = path.slice(1);
    else if (path.startsWith("/watch")) videoId = u.searchParams.get("v");
    else if (path.startsWith("/shorts/")) videoId = path.split("/")[2];
    else if (path.startsWith("/embed/")) videoId = path.split("/")[2];
    if (videoId) {
      return {
        type: "youtube",
        // youtube-nocookie.com is Google's tracker-free embed domain — no sandbox
        // (would break fullscreen + playback controls), no strict referrer policy
        // (some videos refuse to load without an origin).
        src: `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`,
        aspect: "16/9"
      };
    }
  }

  // X / Twitter — rendered via react-tweet (no widgets.js, no trackers)
  if (host === "x.com" || host === "twitter.com" || host === "mobile.twitter.com") {
    const m = path.match(/^\/([^/]+)\/status\/(\d+)/);
    if (m) return { type: "twitter", url, statusId: m[2], username: m[1] };
  }

  // Spotify
  if (host === "open.spotify.com") {
    const m = path.match(/^\/(track|album|playlist|episode|show|artist)\/([A-Za-z0-9]+)/);
    if (m) {
      return {
        type: "spotify",
        src: `https://open.spotify.com/embed/${m[1]}/${m[2]}`,
        aspect: m[1] === "track" || m[1] === "episode" ? "16/5" : "16/9"
      };
    }
  }

  // SoundCloud
  if (host === "soundcloud.com" || host === "m.soundcloud.com") {
    const cleanUrl = `https://soundcloud.com${path}`;
    return {
      type: "soundcloud",
      src: `https://w.soundcloud.com/player/?url=${encodeURIComponent(cleanUrl)}&color=%23FF3300&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false`,
      aspect: "16/5"
    };
  }

  // TikTok — official public iframe embed endpoint (no embed.js tracker script)
  if (host === "tiktok.com" || host === "vm.tiktok.com" || host === "vt.tiktok.com") {
    const m = path.match(/\/([^/]+)\/video\/(\d+)/);
    const username = m ? m[1] : null;
    const videoId = m ? m[2] : null;
    if (videoId) {
      return {
        type: "tiktok",
        url,
        videoId,
        username,
        src: `https://www.tiktok.com/embed/v2/${videoId}?lang=en-US`
      };
    }
    // Short-link (vm.tiktok.com/xxx) — can't extract videoId client-side, fall back to preview card
    return { type: "tiktok-link", url };
  }

  // Instagram — official public iframe embed endpoint (no embed.js tracker script)
  if (host === "instagram.com") {
    const m = path.match(/^\/(p|reel|tv)\/([^/?]+)/);
    if (m) {
      return {
        type: "instagram",
        url,
        mediaType: m[1],
        mediaId: m[2],
        src: `https://www.instagram.com/${m[1]}/${m[2]}/embed/`
      };
    }
  }

  // Apple Music
  if (host === "music.apple.com") {
    return {
      type: "apple-music",
      src: `https://embed.music.apple.com${path}${u.search}`,
      aspect: path.includes("/song") || path.includes("?i=") ? "16/5" : "16/9"
    };
  }

  // Bandcamp — static link card only
  if (host.endsWith(".bandcamp.com")) {
    return { type: "bandcamp-link", url };
  }

  return null;
};

const TwitterEmbed = ({ statusId }) => {
  if (!statusId) return null;
  return (
    <div className="mt-3 rounded-xl overflow-hidden bg-[#050810] flex justify-center" data-theme="dark" data-testid="twitter-embed">
      <Tweet id={statusId} />
    </div>
  );
};

// Static privacy-friendly link card, used only as a fallback when we can't
// extract enough info to render an embed (e.g. TikTok short-links like vm.tiktok.com/xyz).
const StaticPreviewCard = ({ url, platform, label, testId, iconColor }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer nofollow"
    className="mt-3 flex items-center gap-3 p-4 rounded-xl border border-[#1A2030] bg-[#050810] hover:border-[#EC008C]/50 transition-colors"
    data-testid={testId}
  >
    <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: iconColor + "22", color: iconColor }}>
      <span className="font-['Unbounded'] font-bold text-sm">{platform.charAt(0)}</span>
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs uppercase tracking-wider text-[#8A92A0] mb-0.5">{platform}</p>
      <p className="text-sm text-white truncate">{label}</p>
    </div>
    <ExternalLink className="w-4 h-4 text-[#8A92A0] flex-shrink-0" />
  </a>
);

// TikTok — renders TikTok's own iframe player at a portrait aspect ratio. No
// embed.js script is loaded on the parent page (privacy-friendly).
const TikTokEmbed = ({ src }) => (
  <div className="mt-3 rounded-xl overflow-hidden border border-[#1A2030] bg-[#050810] mx-auto" style={{ maxWidth: "340px" }} data-testid="tiktok-embed">
    <iframe
      src={src}
      title="TikTok"
      allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
      allowFullScreen
      loading="lazy"
      referrerPolicy="strict-origin-when-cross-origin"
      className="w-full"
      style={{ aspectRatio: "9/16", minHeight: "540px" }}
      frameBorder="0"
    />
  </div>
);

// Instagram — renders Instagram's public /embed/ iframe. No embed.js tracker
// is loaded on the parent page.
const InstagramEmbed = ({ src, mediaType }) => (
  <div className="mt-3 rounded-xl overflow-hidden border border-[#1A2030] bg-[#050810] mx-auto" style={{ maxWidth: "540px" }} data-testid="instagram-embed">
    <iframe
      src={src}
      title="Instagram"
      allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
      allowFullScreen
      loading="lazy"
      referrerPolicy="strict-origin-when-cross-origin"
      className="w-full"
      style={{ minHeight: mediaType === "reel" ? "720px" : "620px" }}
      frameBorder="0"
      scrolling="no"
    />
  </div>
);

const TikTokLinkFallback = ({ url }) => (
  <StaticPreviewCard
    url={url}
    platform="TikTok"
    label="Watch on TikTok"
    testId="tiktok-embed"
    iconColor="#32D583"
  />
);

const IFrameEmbed = ({ src, aspect = "16/9", title }) => (
  <div className="mt-3 rounded-xl overflow-hidden border border-[#1A2030] bg-[#050810]" style={{ aspectRatio: aspect }} data-testid="iframe-embed">
    <iframe
      src={src}
      title={title || "Embedded content"}
      allow="autoplay; encrypted-media; clipboard-write; fullscreen; picture-in-picture; web-share"
      allowFullScreen
      loading="lazy"
      referrerPolicy="strict-origin-when-cross-origin"
      className="w-full h-full"
      frameBorder="0"
    />
  </div>
);

const PlatformEmbed = ({ url, meta }) => {
  const embed = detectEmbed(url);
  if (!embed) return <LinkPreviewCard url={url} meta={meta} />;

  switch (embed.type) {
    case "youtube":
      return <IFrameEmbed src={embed.src} aspect={embed.aspect} title="YouTube" />;
    case "spotify":
      return <IFrameEmbed src={embed.src} aspect={embed.aspect} title="Spotify" />;
    case "soundcloud":
      return <IFrameEmbed src={embed.src} aspect={embed.aspect} title="SoundCloud" />;
    case "apple-music":
      return <IFrameEmbed src={embed.src} aspect={embed.aspect} title="Apple Music" />;
    case "twitter":
      return <TwitterEmbed statusId={embed.statusId} />;
    case "tiktok":
      return <TikTokEmbed src={embed.src} />;
    case "tiktok-link":
      return <TikTokLinkFallback url={url} />;
    case "instagram":
      return <InstagramEmbed src={embed.src} mediaType={embed.mediaType} />;
    case "bandcamp-link":
    default:
      return <LinkPreviewCard url={url} meta={meta} />;
  }
};

export default PlatformEmbed;
